import { useEffect, useState } from "react";
import { Sliders, Save, RotateCcw } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// PricingGridPanel — admin settings for the XV pricing grid
// (categories + success fees). Opens from CockpitFinance.
// ─────────────────────────────────────────────────────────────

export default function PricingGridPanel({ onClose }) {
    const [grid, setGrid] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    useEffect(() => {
        cockpit
            .get("/settings/pricing-grid")
            .then(({ data }) => setGrid(data.pricing_grid))
            .catch((e) =>
                setErr(e?.response?.data?.detail || "Chargement impossible"),
            );
    }, []);

    if (!grid)
        return (
            <div
                data-testid="pricing-grid-panel"
                className="bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10 text-[12px] text-[#f4ecd8]/40"
            >
                Chargement de la grille tarifaire…
            </div>
        );

    const updateCategory = (i, key, value) => {
        const next = { ...grid };
        next.categories = grid.categories.map((c, idx) =>
            idx === i ? { ...c, [key]: value } : c,
        );
        setGrid(next);
    };
    const updateSuccess = (i, key, value) => {
        const next = { ...grid };
        next.success_fees = grid.success_fees.map((s, idx) =>
            idx === i ? { ...s, [key]: value } : s,
        );
        setGrid(next);
    };

    const save = async () => {
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const payload = {
                pricing_grid: {
                    ...grid,
                    categories: grid.categories.map((c) => ({
                        ...c,
                        fees_min: Number(c.fees_min) || 0,
                        fees_max: Number(c.fees_max) || 0,
                    })),
                    success_fees: grid.success_fees.map((s) => ({
                        ...s,
                        pct_min: Number(s.pct_min) || 0,
                        pct_max: Number(s.pct_max) || 0,
                    })),
                },
            };
            const { data } = await cockpit.put(
                "/settings/pricing-grid",
                payload,
            );
            setGrid(data.pricing_grid);
            setMsg("Grille enregistrée.");
            setTimeout(() => setMsg(""), 3000);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            data-testid="pricing-grid-panel"
            className="mb-12 bg-white/[0.02] border border-white/[0.06] p-8 lg:p-10"
        >
            <div className="flex items-baseline justify-between mb-7 gap-4 flex-wrap">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                        <Sliders strokeWidth={1.1} size={11} />
                        Grille Tarifaire XV
                    </div>
                    <h2
                        className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Catégories d'honoraires & success fees
                    </h2>
                </div>
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#d4af37] transition-colors duration-500"
                    >
                        Fermer
                    </button>
                )}
            </div>

            {/* Categories */}
            <div className="mb-10">
                <div className="overline text-[#d4af37]/55 mb-4">
                    Catégories de mission
                </div>
                <div className="border border-white/[0.06] divide-y divide-white/[0.04]">
                    {grid.categories.map((c, i) => (
                        <div
                            key={c.key}
                            data-testid={`grid-cat-${c.key}`}
                            className="grid lg:grid-cols-[1fr_auto_auto_2fr] gap-5 px-5 py-5 items-center"
                        >
                            <div>
                                <div className="overline text-[#d4af37]/60 mb-1">
                                    {c.key}
                                </div>
                                <div
                                    className="font-serif text-[16px] text-[#f4ecd8] leading-tight"
                                    style={{ fontWeight: 400 }}
                                >
                                    {c.label}
                                </div>
                            </div>
                            <div className="w-32">
                                <label className="block text-[9.5px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                    Min €
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={500}
                                    value={c.fees_min}
                                    onChange={(e) =>
                                        updateCategory(i, "fees_min", e.target.value)
                                    }
                                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[14px] font-serif text-[#f4ecd8] tabular-nums"
                                />
                            </div>
                            <div className="w-32">
                                <label className="block text-[9.5px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                    Max €
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={500}
                                    value={c.fees_max}
                                    onChange={(e) =>
                                        updateCategory(i, "fees_max", e.target.value)
                                    }
                                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[14px] font-serif text-[#f4ecd8] tabular-nums"
                                />
                            </div>
                            <input
                                type="text"
                                value={c.note || ""}
                                placeholder="Note interne…"
                                onChange={(e) =>
                                    updateCategory(i, "note", e.target.value)
                                }
                                className="w-full bg-transparent border-b border-white/[0.08] focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif italic text-[#f4ecd8]/70"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Success fees */}
            <div className="mb-10">
                <div className="overline text-[#d4af37]/55 mb-4">
                    Grille success fees
                </div>
                <div className="border border-white/[0.06] divide-y divide-white/[0.04]">
                    {grid.success_fees.map((s, i) => (
                        <div
                            key={s.key}
                            className="grid lg:grid-cols-[1fr_auto_auto_2fr] gap-5 px-5 py-5 items-center"
                        >
                            <div
                                className="font-serif text-[15px] text-[#f4ecd8]"
                                style={{ fontWeight: 400 }}
                            >
                                {s.label}
                            </div>
                            <div className="w-24">
                                <label className="block text-[9.5px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                    % min
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={s.pct_min}
                                    onChange={(e) =>
                                        updateSuccess(i, "pct_min", e.target.value)
                                    }
                                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[14px] font-serif text-[#f4ecd8] tabular-nums"
                                />
                            </div>
                            <div className="w-24">
                                <label className="block text-[9.5px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                    % max
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={0.1}
                                    value={s.pct_max}
                                    onChange={(e) =>
                                        updateSuccess(i, "pct_max", e.target.value)
                                    }
                                    className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-1.5 text-[14px] font-serif text-[#f4ecd8] tabular-nums"
                                />
                            </div>
                            <input
                                type="text"
                                value={s.note || ""}
                                placeholder="Précision…"
                                onChange={(e) =>
                                    updateSuccess(i, "note", e.target.value)
                                }
                                className="w-full bg-transparent border-b border-white/[0.08] focus:border-[#d4af37] outline-none py-1.5 text-[13px] font-serif italic text-[#f4ecd8]/70"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Philosophy */}
            <div className="mb-8">
                <label className="block overline text-[#d4af37]/55 mb-3">
                    Philosophie XV (rappel interne)
                </label>
                <textarea
                    value={grid.philosophy || ""}
                    onChange={(e) =>
                        setGrid({ ...grid, philosophy: e.target.value })
                    }
                    rows={3}
                    className="w-full bg-transparent border border-white/[0.08] focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif italic text-[#f4ecd8]/85 leading-relaxed"
                />
            </div>

            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1 mb-4">
                    {err}
                </div>
            )}
            {msg && (
                <div className="text-[12px] text-[#a3d39c] border-l-2 border-[#a3d39c]/50 pl-4 py-1 mb-4">
                    {msg}
                </div>
            )}

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={save}
                    disabled={busy}
                    data-testid="pricing-grid-save"
                    className="inline-flex items-center gap-3 px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                >
                    {busy ? <RotateCcw className="animate-spin" size={13} /> : <Save strokeWidth={1.2} size={13} />}
                    {busy ? "Enregistrement…" : "Enregistrer la grille"}
                </button>
            </div>
        </section>
    );
}
